// An agent's schedule in friendly terms, and back. The hub reads a schedule string (agent-hub src/schedule.ts):
// "continuous", "every <n>m|h|d" (at least 5 minutes, counted from when its last run started), or cron expressions in
// Pacific time, several separated by ";". "none" is this page's "not scheduled". Each friendly mode becomes one of those;
// a string no mode can show opens as Advanced (cron).
import { CronExpressionParser } from 'cron-parser';

export const TZ = 'America/Los_Angeles';
export const NO_SCHEDULE = 'none';
const EVERY_RE = /^every\s+(\d+)\s*(m|min|h|hr|d)$/i;
const UNIT_MS: Record<string, number> = { m: 60_000, min: 60_000, h: 3_600_000, hr: 3_600_000, d: 86_400_000 };

export interface Time { h: number; m: number }
export type Friendly =
  | { mode: 'none' }
  | { mode: 'continuous' }
  | { mode: 'every'; n: number; unit: 'm' | 'h'; window: { from: number; to: number } | null } // window: Pacific hours, inclusive
  | { mode: 'daily'; times: Time[] }
  | { mode: 'weekly'; days: number[]; time: Time } // 0 = Sunday
  | { mode: 'monthly'; day: number; time: Time }
  | { mode: 'cron'; text: string };
export type Mode = Friendly['mode'];

/** Minute steps a window can use: cron's "*\/n" restarts each hour, so only steps that divide an hour repeat evenly. */
export const WINDOW_MINUTE_STEPS = [5, 10, 15, 20, 30];

/** Why the hub couldn't read this schedule, or null when it can (the hub's own rules and messages). */
export function scheduleProblem(s: string): string | null {
  const t = s.trim();
  if (t === NO_SCHEDULE || t === 'continuous') return null;
  const every = EVERY_RE.exec(t);
  if (every) return Number(every[1]) * UNIT_MS[every[2].toLowerCase()] < 5 * 60_000 ? 'every must be at least 5 minutes' : null;
  const exprs = crons(t);
  if (!exprs.length) return 'the schedule is empty';
  for (const e of exprs) {
    try { CronExpressionParser.parse(e, { tz: TZ }); } catch (err) { return `"${e}" is not a cron expression: ${(err as Error).message}`; }
  }
  return null;
}

const crons = (s: string) => s.split(';').map(x => x.trim()).filter(Boolean);
const num = (x: string) => Number(x);
const sortTimes = (ts: Time[]) => [...ts].sort((a, b) => a.h * 60 + a.m - (b.h * 60 + b.m));

/** "1-5" or "1,3,5" (cron day-of-week, 7 = Sunday too) as days, or null when it's something else. */
function cronDays(f: string): number[] | null {
  const out = new Set<number>();
  for (const part of f.split(',')) {
    const r = /^(\d)(?:-(\d))?$/.exec(part);
    if (!r) return null;
    for (let d = num(r[1]); d <= num(r[2] ?? r[1]); d++) out.add(d % 7);
  }
  return [...out].sort();
}

/** The friendly form of a stored schedule; Advanced (cron) when none fits. */
export function fromSchedule(raw: string | null | undefined): Friendly {
  const s = (raw ?? '').trim();
  if (!s || s === NO_SCHEDULE) return { mode: 'none' };
  if (s === 'continuous') return { mode: 'continuous' };
  const every = EVERY_RE.exec(s);
  if (every) {
    const u = every[2].toLowerCase();
    if (u === 'd') return { mode: 'every', n: num(every[1]) * 24, unit: 'h', window: null };
    return { mode: 'every', n: num(every[1]), unit: u.startsWith('m') ? 'm' : 'h', window: null };
  }
  const exprs = crons(s);
  let m: RegExpExecArray | null;
  if (exprs.length === 1) {
    const e = exprs[0].replace(/\s+/g, ' ');
    if ((m = /^\*\/(\d+) (\d+)-(\d+) \* \* \*$/.exec(e)) && WINDOW_MINUTE_STEPS.includes(num(m[1]))) return { mode: 'every', n: num(m[1]), unit: 'm', window: { from: num(m[2]), to: num(m[3]) } };
    if ((m = /^0 (\d+)-(\d+)\/(\d+) \* \* \*$/.exec(e))) return { mode: 'every', n: num(m[3]), unit: 'h', window: { from: num(m[1]), to: num(m[2]) } };
    if ((m = /^(\d+) (\d+) (\d+) \* \*$/.exec(e))) return { mode: 'monthly', day: num(m[3]), time: { h: num(m[2]), m: num(m[1]) } };
    if ((m = /^(\d+) (\d+) \* \* ([\d,-]+)$/.exec(e))) {
      const days = cronDays(m[3]);
      if (days && days.length < 7) return { mode: 'weekly', days, time: { h: num(m[2]), m: num(m[1]) } };
      if (days) return { mode: 'daily', times: [{ h: num(m[2]), m: num(m[1]) }] };
    }
  }
  // Daily: every cron "M H[,H…] * * *".
  const times: Time[] = [];
  for (const e of exprs) {
    if (!(m = /^(\d+) (\d+(?:,\d+)*) \* \* \*$/.exec(e.replace(/\s+/g, ' ')))) return { mode: 'cron', text: s };
    for (const h of m[2].split(',')) times.push({ h: num(h), m: num(m[1]) });
  }
  return times.length ? { mode: 'daily', times: sortTimes(times) } : { mode: 'cron', text: s };
}

/** The schedule string the hub reads (and the page stores) for a friendly form. */
export function toSchedule(f: Friendly): string {
  switch (f.mode) {
    case 'none': return NO_SCHEDULE;
    case 'continuous': return 'continuous';
    case 'cron': return f.text.trim();
    case 'every':
      if (!f.window) return `every ${f.n}${f.unit}`;
      return f.unit === 'm' ? `*/${f.n} ${f.window.from}-${f.window.to} * * *` : `0 ${f.window.from}-${f.window.to}/${f.n} * * *`;
    case 'daily': {
      // Times that share a minute go in one cron ("0 7,16 * * *"); the rest each get their own.
      const byMinute = new Map<number, number[]>();
      for (const t of sortTimes(f.times)) byMinute.set(t.m, [...(byMinute.get(t.m) ?? []), t.h]);
      return [...byMinute].map(([m, hs]) => `${m} ${[...new Set(hs)].join(',')} * * *`).join('; ');
    }
    case 'weekly': return `${f.time.m} ${f.time.h} * * ${[...f.days].sort().join(',')}`;
    case 'monthly': return `${f.time.m} ${f.time.h} ${f.day} * *`;
  }
}

/** What a mode starts with when picked: the current schedule's own values where they carry over. */
export function switchMode(cur: Friendly, mode: Mode): Friendly {
  if (cur.mode === mode) return cur;
  const time: Time = cur.mode === 'daily' ? cur.times[0] : cur.mode === 'weekly' || cur.mode === 'monthly' ? cur.time : { h: 7, m: 0 };
  switch (mode) {
    case 'none': return { mode };
    case 'continuous': return { mode };
    case 'every': return { mode, n: 1, unit: 'h', window: null };
    case 'daily': return { mode, times: [time] };
    case 'weekly': return { mode, days: [1, 2, 3, 4, 5], time };
    case 'monthly': return { mode, day: 1, time };
    case 'cron': {
      const s = toSchedule(cur);
      return { mode, text: cur.mode === 'none' ? `${time.m} ${time.h} * * *` : s };
    }
  }
}

export const fmtClock = ({ h, m }: Time) => `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ordinal = (n: number) => `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
const list = (xs: string[]) => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

function daysLabel(days: number[]): string {
  const k = [...days].sort().join();
  if (k === '1,2,3,4,5') return 'Weekdays';
  if (k === '0,6') return 'Weekends';
  return list(days.map(d => DAY[d]));
}

/** The schedule in one plain sentence, times in Pacific. */
export function describeSchedule(raw: string | null | undefined): string {
  const f = fromSchedule(raw);
  switch (f.mode) {
    case 'none': return 'Not scheduled';
    case 'continuous': return 'Whenever there is room';
    case 'every':
      if (!f.window) return `Every ${plural(f.n, f.unit === 'm' ? 'minute' : 'hour')}, counted from when its last run started`;
      return `Every ${plural(f.n, f.unit === 'm' ? 'minute' : 'hour')}, ${fmtClock({ h: f.window.from, m: 0 })} to ${fmtClock(f.unit === 'm' ? { h: f.window.to, m: 60 - f.n } : { h: f.window.to - ((f.window.to - f.window.from) % f.n), m: 0 })} PT`;
    case 'daily': return `Daily at ${list(f.times.map(fmtClock))} PT`;
    case 'weekly': return `${daysLabel(f.days)} at ${fmtClock(f.time)} PT`;
    case 'monthly': return `Monthly on the ${ordinal(f.day)} at ${fmtClock(f.time)} PT`;
    case 'cron': return `Custom schedule: ${f.text}`;
  }
}

/**
 * Its next runs as the hub would start them (agent-hub schedule.ts nextDue): a cron's coming slots across all its
 * expressions; "every" at the earliest that long after the last run started (now, if it's already due). An agent the
 * pool is busy for starts late, so these are the earliest times.
 */
export function nextRuns(raw: string | null | undefined, count: number, now: Date, lastStartedAt: Date | null = null): Date[] {
  const s = (raw ?? '').trim();
  if (!s || s === NO_SCHEDULE || s === 'continuous' || scheduleProblem(s)) return [];
  const every = EVERY_RE.exec(s);
  if (every) {
    const ms = Number(every[1]) * UNIT_MS[every[2].toLowerCase()];
    const first = !lastStartedAt || now.getTime() - lastStartedAt.getTime() >= ms ? now.getTime() : lastStartedAt.getTime() + ms;
    return Array.from({ length: count }, (_x, i) => new Date(first + i * ms));
  }
  const times = new Set<number>();
  for (const e of crons(s)) {
    const it = CronExpressionParser.parse(e, { currentDate: now, tz: TZ });
    for (let i = 0; i < count; i++) times.add(it.next().toDate().getTime());
  }
  return [...times].sort((a, b) => a - b).slice(0, count).map(t => new Date(t));
}

export const fmtPacific = (d: Date) => d.toLocaleString('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
